/**
 * BD Cart Bundles
 * Handles quantity sync and atomic removal for bundled cart line items.
 * Bundles are pairs of parent (garment) + child (patches) lines sharing a _bundle_id property.
 */

const CART_UPDATE_URL = '/cart/update.js';

/**
 * POST to /cart/update.js with the given key→quantity map.
 * Includes section IDs so Shopify returns re-rendered HTML.
 * After success, dispatches 'cart:update' to trigger the theme's section re-render.
 */
async function updateCart(updates) {
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });

  const response = await fetch(CART_UPDATE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      updates,
      sections: Array.from(sectionIds).join(','),
      sections_url: window.location.pathname,
    }),
  });

  const data = await response.json();
  if (data.errors) throw new Error(data.errors);

  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        data: {
          sections: data.sections || {},
          itemCount: data.item_count,
        },
      },
    })
  );

  return data;
}

function updateBundleQuantity(parentKey, childKey, patchesPerBundle, newQty) {
  const updates = { [parentKey]: newQty };
  if (childKey) updates[childKey] = newQty * patchesPerBundle;
  return updateCart(updates);
}

function removeBundle(parentKey, childKey) {
  const updates = { [parentKey]: 0 };
  if (childKey) updates[childKey] = 0;
  return updateCart(updates);
}

function setRowDisabled(row, disabled) {
  if (!row) return;
  row.style.opacity = disabled ? '0.5' : '';
  row.style.pointerEvents = disabled ? 'none' : '';
}

document.addEventListener('click', (event) => {
  // Remove button
  const removeBtn = event.target.closest('[data-bd-bundle-remove]');
  if (removeBtn) {
    event.preventDefault();
    const row = removeBtn.closest('[data-bundle-row]');
    setRowDisabled(row, true);
    const { parentKey, childKey } = removeBtn.dataset;
    removeBundle(parentKey, childKey).catch((err) => {
      console.error('[bd-cart-bundles] remove error:', err);
      setRowDisabled(row, false);
    });
    return;
  }

  // Quantity +/- buttons
  const incBtn = event.target.closest('[data-bd-bundle-qty-inc]');
  const decBtn = event.target.closest('[data-bd-bundle-qty-dec]');
  const btn = incBtn || decBtn;
  if (!btn) return;

  const row = btn.closest('[data-bundle-row]');
  if (!row) return;
  const input = row.querySelector('[data-bd-bundle-qty]');
  if (!input) return;

  const currentQty = parseInt(input.value, 10) || 1;
  const newQty = incBtn ? currentQty + 1 : Math.max(1, currentQty - 1);
  if (newQty === currentQty) return;

  input.value = newQty;
  setRowDisabled(row, true);

  const { parentKey, childKey } = input.dataset;
  const patchesPerBundle = parseInt(input.dataset.patchesPerBundle, 10) || 0;

  updateBundleQuantity(parentKey, childKey, patchesPerBundle, newQty).catch((err) => {
    console.error('[bd-cart-bundles] qty update error:', err);
    input.value = currentQty;
    setRowDisabled(row, false);
  });
});
